import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RelativeTimePipe } from '../../../pipes/relative-time.pipe';
import { ActivityItem } from '../../models/dashboard.model';

type ColorVar = '--to-green-dot' | '--to-brand';

interface EntryVisual {
  icon: string;
  colorVar: ColorVar;
}

const TYPE_VISUALS: Readonly<Record<string, EntryVisual>> = {
  TEST_CASE: { icon: 'description', colorVar: '--to-green-dot' },
  TEST_RUN: { icon: 'check', colorVar: '--to-green-dot' },
  REQUIREMENT: { icon: 'edit', colorVar: '--to-brand' },
};

const DEFAULT_VISUAL: EntryVisual = { icon: 'check', colorVar: '--to-brand' };

@Component({
  selector: 'app-activity-entry',
  imports: [DatePipe, NgTemplateOutlet, RouterLink, MatIcon, TranslatePipe, RelativeTimePipe],
  templateUrl: './activity-entry.component.html',
  styleUrl: './activity-entry.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityEntryComponent {
  private readonly translate = inject(TranslateService);

  item = input.required<ActivityItem>();

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  protected readonly locale = computed(() => this.currentLang() || 'de');

  protected readonly visual = computed<EntryVisual>(() => TYPE_VISUALS[this.item().itemType] ?? DEFAULT_VISUAL);

  protected readonly tint = computed(() => `color-mix(in srgb, var(${this.visual().colorVar}) 15%, transparent)`);

  protected readonly color = computed(() => `var(${this.visual().colorVar})`);

  protected readonly link = computed<unknown[] | null>(() => {
    const value = this.item();
    if (value.itemType !== 'TEST_CASE' || !value.projectId) {
      return null;
    }
    return ['/projects', value.projectId, 'test-cases', value.targetId, 'edit'];
  });
}
